import { useCallback, useEffect, useState } from "react";

/**
 * Manages the "tc" (terms & conditions / show-section toggles) state
 * for the CreatePromo form.
 *
 * - Initializes from the promo's rateRule.ruleVO fields.
 * - On edit/duplicate, merges any newly-set fields without clearing
 *   already-shown sections.
 */
export const useCreatePromoTcState = ({ promo, isEdit, isDuplicate }) => {
  // Extract duplicate logic for calculating tc state
  const calculateTcState = useCallback(
    (preserveLT = false, preserveLOS = false) => {
      return {
        showBooking: !!(
          promo?.rateRule?.ruleVO?.bookingVO?.bookingStartDate ||
          promo?.rateRule?.ruleVO?.bookingVO?.bookingEndDate
        ),
        showPSD: !!(
          promo?.rateRule?.ruleVO?.stayVO?.stayStartDate ||
          promo?.rateRule?.ruleVO?.stayVO?.stayEndDate
        ),
        showBlackouts:
          !!promo?.rateRule?.ruleVO?.stayVO?.blackOutDateRanges?.length,
        showLT: preserveLT
          ? undefined
          : !!(
              promo?.rateRule?.ruleVO?.bookingVO?.minBookingLeadTime ||
              promo?.rateRule?.ruleVO?.bookingVO?.maxBookingLeadTime
            ),
        showLOS: preserveLOS
          ? undefined
          : !!(
              promo?.rateRule?.ruleVO?.stayVO?.minLengthOfStay ||
              promo?.rateRule?.ruleVO?.stayVO?.maxLengthOfStay
            ),
      };
    },
    [promo]
  );

  const [tc, setTc] = useState(() => calculateTcState());

  // Update tc state when promo changes (for editing/duplicating)
  useEffect(() => {
    if (isEdit || isDuplicate) {
      setTc((prevTc) => {
        const newTc = calculateTcState(false, false);
        return {
          showBooking: prevTc.showBooking || newTc.showBooking,
          showPSD: prevTc.showPSD || newTc.showPSD,
          showBlackouts: prevTc.showBlackouts || newTc.showBlackouts,
          showLT: prevTc.showLT || newTc.showLT,
          showLOS: prevTc.showLOS || newTc.showLOS,
        };
      });
    }
  }, [isEdit, isDuplicate, promo, calculateTcState]);

  return { tc, setTc };
};
